<template>
  <div class="row mb-2">
    <div class="col">
      <div class="d-flex justify-content-between flex-column flex-lg-row align-items-end">
        <SelectButton v-model="selectedFilter" :options="filterOptions" id="shipmentFilter">
          <template #option="slotProps">
            <div :class="[getLabelClass(slotProps.option, 'bg-'), slotProps.option !== 'Pending' ? 'text-white' : 'text-dark']" class="px-3 py-1 tab-content">
              {{ slotProps.option }} ({{ countOfToolsInFilter(slotProps.option) }})
            </div>
          </template>
        </SelectButton>

        <div class="search-input-wrapper mt-3 m-w-100">
          <input v-model="search" class="form-control form-control-lg" placeholder="Search">
          <span v-if="search" @click="clearSearch">x</span>
        </div>
      </div>
      <div v-if="selectedFilter === 'Delivered' && isMtd()" class="form-check mt-3 pl-0">
        <input type="checkbox" class="mr-2" id="show-added-to-inventory" v-model="showNotAddedToInventoryOnly">
        <label for="show-added-to-inventory">Show Not Added To Inventory Only</label>
      </div>
    </div>
  </div>

  <DataTable :value="filteredTools"
             responsiveLayout="stack" breakpoint="992px"
             class="p-datatable-sm"
             :rowClass="() => { return 'table-row' }" stripedRows>
    <Column class="image-col" header="">
      <template #body="slotProps">
        <div class="flex-center">
          <img :src="slotProps.data.image_url" class="product-image">
        </div>
      </template>
    </Column>
    <Column class="text-nowrap" field="id" header="Order #"></Column>
    <Column header="Name">
      <template #body="slotProps">
        <inertia-link v-if="slotProps.data.link_product_id" :href="route(productRoute, slotProps.data.link_product_id)">{{ slotProps.data.sku }}</inertia-link> <span v-else>{{ slotProps.data.sku }}</span> - {{ slotProps.data.name }}
        <div v-if="slotProps.data.bundle" class="ml-1">
            <span class="item-subtext">
              From bundle {{ slotProps.data.bundle.name }}
            </span>
        </div>
        <div v-if="slotProps.data.original" class="ml-1">
            <span class="item-subtext">
              Substituted for {{ slotProps.data.original }}
            </span>
        </div>
        <div v-if="slotProps.data.additional_shipment_items.length" class="ml-1">
            <span v-for="item in slotProps.data.additional_shipment_items" class="item-subtext">
              {{ item.qty }} additional item(s) from order
              <span :class="getLabelClass(item.status, 'pt-1 badge badge-')">
                {{ item.status }}
              </span>
            </span>
        </div>

        <div v-if="isMtd && slotProps.data.show_add_to_inventory" class="clearfix">
          <inertia-link v-if="!slotProps.data.mtd_inventory_adjusted" @click="addToInventory(slotProps.data.shipment_item_id)" class="text-nowrap" preserve-scroll>
            Add to Inventory (Current: {{ slotProps.data.inventory }})
          </inertia-link>
          <span v-else>Added to Inventory</span>
        </div>
      </template>
    </Column>
    <Column field="order_date" header="Ordered">
      <template #body="slotProps">
        {{ moment(slotProps.data.order_date).format('MM/DD/YYYY') }}
      </template>
    </Column>
    <Column header="Destination">
      <template #body="slotProps">
        <span v-if="slotProps.data.address">
          {{ slotProps.data.address.address }}<br>
          {{ slotProps.data.address.state }}, {{ slotProps.data.address.city }} {{ slotProps.data.address.postal_code }}
        </span>
        <span v-else-if="slotProps.data.order_mtd_company_name">
          {{ slotProps.data.order_mtd_company_name }}
        </span>
      </template>
    </Column>
    <Column field="qty" header="Quantity"></Column>
    <Column field="" header="Status">
      <template #body="slotProps">
        <span :class="getLabelClass(slotProps.data.status, 'pt-1 badge badge-')">
          {{ slotProps.data.status }}
          <span v-if="slotProps.data.backorder_eta_at">
            <br>Expected to ship {{ moment.utc(slotProps.data.backorder_eta_at).format('MM/DD/YYYY') }}
          </span>
        </span>
      </template>
    </Column>
    <Column>
      <template #body="slotProps">
        <div class="d-flex">
          <button v-if="slotProps.data.user_shipment_id && slotProps.data.tracking_number" class="btn btn-secondary mr-2 btn-sm"
                  @click="showShippingEvents(slotProps.data.user_shipment_id)">
            <i class="fa fa-shipping-fast"></i>
          </button>

          <button v-if="search !== slotProps.data.name"
                  class="btn btn-secondary mr-2 btn-sm"
                  @click="filterBySku(slotProps.data.sku)">
            <i class="fa fa-filter"></i>
          </button>

          <inertia-link class="btn btn-secondary btn-sm"
                        :href="getViewOrderLink(slotProps.data.order_id)">
            <i class="fa fa-eye"></i>
          </inertia-link>
        </div>
      </template>
    </Column>
  </DataTable>

  <ShippingEvents :shipmentId="viewShipmentId" @close="viewShipmentId = null"></ShippingEvents>
</template>

<script>
import moment from 'moment';
import SelectButton from 'primevue/selectbutton'
import ShippingEvents from '@/Components/Modals/ShippingEvents'

export default {
  components: {
    ShippingEvents,
    SelectButton,
  },
  props: {
    tools: Array,
  },
  data() {
    return {
      filteredTools: this.tools,
      search: '',
      selectedFilter: 'All',
      productRoute: this.isMtd() ? 'mtd.wholesale.view' : 'products.view',
      filterOptions: [
        'All',
        'Pending',
        'In Transit',
        'Backorder',
        'Delivered',
      ],
      viewShipmentId: null,
      showNotAddedToInventoryOnly: false,
    };
  },
  created() {
    this.moment = moment
  },
  watch: {
    selectedFilter() {
      this.showNotAddedToInventoryOnly = false;
      this.filterList();
    },
    search() {
      this.filterList();
    },
    showNotAddedToInventoryOnly() {
      this.filterList();
    },
  },
  methods: {
    filterList() {
      let filteredList = this.tools;

      if (this.selectedFilter !== 'All') {
        filteredList = filteredList.filter((tool) => {
          return tool.status === this.selectedFilter;
        });
      }

      if (this.search.length) {
        let search = this.search.toLowerCase();
        filteredList = filteredList.filter((tool) => {
          return tool.sku.toLowerCase().includes(search) ||
            tool.name.toLowerCase().includes(search) ||
            (tool.bundle && tool.bundle.name.toLowerCase().includes(search)) ||
            (tool.bundle && tool.bundle.sku.toLowerCase().includes(search)) ||
            (tool.original && tool.original.toLowerCase().includes(search)) ;
        });
      }
      
      if (this.selectedFilter === 'Delivered' && this.showNotAddedToInventoryOnly) {
        filteredList = filteredList.filter((tool) => {
          return tool.show_add_to_inventory && !tool.mtd_inventory_adjusted;
        });
      }

      this.filteredTools = filteredList;
    },
    filterBySku(term) {
      this.search = term;
    },
    countOfToolsInFilter(status) {
      let tools = this.search ? this.filteredTools : this.tools;

      if (status === 'All') {
        return tools.length;
      }

      return tools.filter((tool) => {
        return tool.status === status;
      }).length;
    },
    getLabelClass(status, style) {
      switch (status) {
        case 'In Transit':
          return style + 'primary';
        case 'Backorder':
          return style + 'danger';
        case 'Delivered':
          return style + 'success';
        case 'Pending':
          return style + 'warning';
        default:
          return style + 'info';
      }
    },
    showShippingEvents(shipmentId) {
      this.viewShipmentId = shipmentId;
    },
    clearSearch() {
      this.search = '';
    },
    getViewOrderLink(orderId) {
      return this.isMtd()
        ? route('mtd.order.view', orderId)
        : route('order.view', orderId);
    },
    addToInventory(shipmentItemId) {
      this.$inertia.form({}).get(route('mtd.orders.adjustinventory', shipmentItemId), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          this.filterList();
        },
      });
    }
  },
}
</script>

<style>
  .search-input-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    margin-left: auto;
  }

  .search-input-wrapper span {
    position: absolute;
    display: block;
    right: 5px;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    color: #fff;
    background-color: #ccc;
    text-align: center;
    line-height: 1em;
    cursor: pointer;
  }

  .search-input-wrapper input {
    padding-right: 18px;
    box-sizing: border-box;
  }

  #shipmentFilter .p-button {
    padding: 0;
  }

  #shipmentFilter .p-button.p-highlight {
    border-bottom: 2px solid rgba(0, 0, 0, 0.85);
  }

  #shipmentFilter .p-button.p-highlight:focus {
    box-shadow: none;
  }

  #shipmentFilter .p-button.p-highlight .tab-content {
    padding-top: 7.5px !important;
  }

  .item-subtext {
    border-left: 2px solid #000;
    padding-left: 5px;
    display: inline-block;
    font-style: italic;
  }
</style>

<style scoped>
  .product-image {
    max-width: 60px;
    object-fit: cover;
    max-height: 60px;
  }
</style>
